import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ArrowRight, FileCheck2, Files, Users, Clock } from "lucide-react";
import { api, fmtDateTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Completeness, StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";

export function StatCard({ label, value, Icon, tone = "teal", testId }) {
  const tones = { teal: "bg-teal-50 text-teal-700", red: "bg-red-50 text-red-700", blue: "bg-blue-50 text-blue-700", emerald: "bg-emerald-50 text-emerald-700", slate: "bg-slate-100 text-slate-700" };
  return (
    <div data-testid={testId} className="clinical-card p-6 sm:p-7 flex items-center gap-5 min-h-[120px]">
      <span className={`w-14 h-14 rounded-2xl grid place-items-center shrink-0 ${tones[tone]}`}><Icon className="w-6 h-6" /></span>
      <div><p className="text-3xl sm:text-4xl font-heading font-extrabold text-slate-900 leading-none">{value ?? "—"}</p><p className="text-sm text-slate-500 font-medium mt-1.5">{label}</p></div>
    </div>
  );
}

export function ClinicianDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState({});
  const [cases, setCases] = useState([]);
  const [onlyRed, setOnlyRed] = useState(false);
  useEffect(() => {
    api.get("/dashboard").then((r) => setStats(r.data));
    api.get("/cases").then((r) => setCases(r.data));
  }, []);
  const pending = cases.filter((c) => ["pending_review", "in_review"].includes(c.status)).filter((c) => !onlyRed || c.red_alerts?.length);
  const redTotal = cases.filter((c) => ["pending_review", "in_review"].includes(c.status) && c.red_alerts?.length).length;

  return (
    <div className="space-y-8" data-testid="doctor-dashboard-view">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div><p className="eyebrow">{user.role === "doctor" ? "Review queue" : "Clinic overview"}{stats.organization && <span className="text-teal-700"> · {stats.organization}</span>}</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 mt-1">Good day, {user.name}</h1></div>
        <Link to="/patients"><Button data-testid="go-to-patients-button" className="bg-teal-700 hover:bg-teal-800 rounded-full">Patients <ArrowRight className="w-4 h-4 ml-1" /></Button></Link>
      </div>
      {redTotal > 0 && (
        <div data-testid="red-alerts-banner" className="rounded-xl border border-red-200 bg-red-50 p-4 flex items-center gap-3">
          <span className="w-2.5 h-2.5 rounded-full bg-red-600 pulse-red" />
          <p className="text-sm text-red-900 font-medium flex-1">{redTotal} case{redTotal > 1 ? "s" : ""} in the queue {redTotal > 1 ? "carry" : "carries"} red-alert findings.</p>
          <button data-testid="red-alert-filter-toggle" onClick={() => setOnlyRed(!onlyRed)} className="text-xs font-semibold text-red-800 underline">{onlyRed ? "Show all" : "Show red alerts only"}</button>
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5 lg:gap-6">
        <StatCard testId="stat-patients" label="Patients" value={stats.patients} Icon={Users} />
        <StatCard testId="stat-pending" label="Pending review" value={stats.pending_review} Icon={Clock} tone="blue" />
        <StatCard testId="stat-red" label="Red-alert cases" value={stats.red_alert_cases} Icon={AlertTriangle} tone="red" />
        <StatCard testId="stat-released" label="Released reports" value={stats.released} Icon={FileCheck2} tone="emerald" />
        <StatCard testId="stat-docs" label="Documents" value={stats.documents} Icon={Files} tone="slate" />
      </div>
      <section className="space-y-3">
        <div className="flex items-center justify-between"><h2 className="font-heading text-lg font-semibold text-slate-800">Awaiting doctor review</h2><span className="text-xs text-slate-500">{pending.length} case(s)</span></div>
        <div data-testid="pending-review-queue-table" className="grid md:grid-cols-2 gap-5">
          {pending.length === 0 && <div className="clinical-card p-10 text-center text-sm text-slate-500 md:col-span-2" data-testid="empty-queue">No cases awaiting review. Create a case from a patient record.</div>}
          {pending.map((c) => (
            <Link to={`/cases/${c.id}`} key={c.id} data-testid={`queue-case-${c.id}`} className="clinical-card p-6 sm:p-7 flex flex-col gap-5 hover:shadow-md">
              <div className="flex items-start justify-between gap-4">
                <div><p className="font-heading font-bold text-lg sm:text-xl text-slate-900">{c.patient_name}</p><p className="text-sm text-slate-500 mt-1">{c.title}</p><p className="text-xs text-slate-400 mt-0.5">created {fmtDateTime(c.created_at)}</p></div>
                <StatusBadge status={c.status} />
              </div>
              <div className="flex flex-wrap items-center gap-3 mt-auto">
                {c.red_alerts?.length > 0 && <span className="badge-pill bg-red-50 text-red-800 border-red-200 pulse-red"><AlertTriangle className="w-3.5 h-3.5" /> {c.red_alerts.length} red alert{c.red_alerts.length > 1 ? "s" : ""}</span>}
                <Completeness value={c.completeness} />
                <span className="ml-auto text-sm font-semibold text-teal-700 flex items-center gap-1">Review <ArrowRight className="w-4 h-4" /></span>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
